import { cn } from "@/utils";
import { ChevronDown, MessageSquare } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { HarnessSegment as HarnessSegmentData } from "./harnessRailItems";
import { HarnessRailItemView } from "./HarnessRailItemView";

function formatDuration(ms: number): string {
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

export function segmentStats(segment: HarnessSegmentData): string {
  const parts: string[] = [];
  if (segment.toolCalls) {
    parts.push(
      `${segment.toolCalls} tool${segment.toolCalls === 1 ? "" : "s"}`,
    );
  }
  if (segment.agents) {
    parts.push(`${segment.agents} agent${segment.agents === 1 ? "" : "s"}`);
  }
  if (
    segment.startMs != null &&
    segment.endMs != null &&
    segment.endMs - segment.startMs >= 1000
  ) {
    parts.push(formatDuration(segment.endMs - segment.startMs));
  }
  return parts.join(" · ");
}

/**
 * One chapter of the rail: the agent's narration line as a header row —
 * chevron, glyph on the rail line, one-line text, right-aligned stats — and,
 * when open, the thoughts / tool runs / agent board it produced, indented on
 * their own connector. `open` is owned by the turn (user overrides win; the
 * current segment is open by default, a finished one closed).
 */
export function HarnessSegmentRow({
  segment,
  open,
  current,
  liveItemKey,
  onToggle,
}: {
  segment: HarnessSegmentData;
  open: boolean;
  /** The segment the turn is working in right now. */
  current: boolean;
  liveItemKey?: string;
  onToggle: () => void;
}) {
  const stats = segmentStats(segment);
  const canToggle = segment.items.length > 0;
  const title = segment.header?.content?.trim() || "Activity";

  return (
    <div data-testid="harness-segment" data-open={open}>
      <button
        type="button"
        onClick={canToggle ? onToggle : undefined}
        aria-expanded={canToggle ? open : undefined}
        disabled={!canToggle}
        className={cn(
          "group flex w-full items-center gap-2 px-4 py-2 text-left outline-none",
          !canToggle && "cursor-default",
        )}
      >
        <span className="relative z-[1] flex size-6 shrink-0 items-center justify-center bg-background">
          <MessageSquare
            className={cn(
              "size-[18px]",
              current ? "text-n-80" : "text-n-60",
              !segment.header && "opacity-60",
            )}
          />
        </span>
        <span
          className={cn(
            "min-w-0 flex-1 truncate font-sans text-sm text-pretty transition-colors duration-150 ease-out",
            current ? "text-n-90" : "text-n-70",
            canToggle && "group-hover:text-n-90",
            !segment.header && "italic text-n-50",
          )}
        >
          {title}
        </span>
        {stats && (
          <span className="shrink-0 whitespace-nowrap font-mono text-xs text-n-50">
            {stats}
          </span>
        )}
        <ChevronDown
          className={cn(
            "size-3.5 shrink-0 text-n-40 transition-all duration-150 ease-out group-hover:text-n-60",
            !canToggle && "invisible",
            open && "rotate-180",
          )}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && canToggle && (
          <motion.div
            key="segment-body"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            {/* Items sit one level in, on their own connector; the outer rail
                line keeps running past the segment headers. */}
            <div className="relative ml-6">
              <span
                aria-hidden
                className="pointer-events-none absolute bottom-5 left-[27px] top-0 w-px bg-n-20"
              />
              {segment.items.map((item) => (
                <HarnessRailItemView
                  key={item.key}
                  item={item}
                  live={item.key === liveItemKey}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
